import basketStore, {BasketStore} from '../../stores/basket/BasketStore';
import httpRequest from '../../utilities/js/httpRequest/httpRequest';
import encodeFormData from '../../utilities/js/encodeFormData/encodeFormData.js';
import BasketItem from '../../types/BasketItem';
import jsGlobalConfig from '../../globals/jsGlobalConfig';
import {CheckoutConfigApiUrls} from '../../components/checkout/jsCheckoutConfig';
import {TrackingService} from '../tracking/TrackingService';
import pigeon from '../../utilities/js/pigeon/pigeon';
import {XiorResponse} from 'xior';

interface BasketServiceUrls extends Partial<CheckoutConfigApiUrls> {
  remove: string,
  miniBasket: string,
}

type BasketStoreWithTracking = BasketStore & {
  trackingData: Record<string, any>
}

class BasketService {
  protected urls: BasketServiceUrls;

  constructor() {
    const basketUrls = window.__checkoutConfig ? window.__checkoutConfig.apiUrls : {};

    this.urls = {
      remove: window.__globalConfig.apiUrls.product.removeFromBasket,
      miniBasket: window.__globalConfig.apiUrls.product.miniBasket,
      ...basketUrls,
    };
  }

  changeQuantityOfProduct(product: BasketItem, qty: number) {
    if (product.splittedId != null) {
      qty += basketStore.basketItemGroups.map(group => group.basketItems)
        .reduce((a, b) => a.concat(b), [])
        .filter(basketItem => basketItem.entryId == product.entryId)
        .filter(basketItem => basketItem.splittedId != product.splittedId)
        .map(basketItem => basketItem.quantity)
        .reduce((a,b) => a += b, 0);
    }
    let formData = {};
    formData['entryId'] = product.entryId;
    formData['newItemId'] = product.itemId;
    formData['quantity'] = qty;
    formData['title'] = document.title;
    formData[window.__globalConfig.csrfToken.name] = window.__globalConfig.csrfToken.value;

    return httpRequest(this.urls.edit, {
      method: 'POST',
      data: encodeFormData(formData)
    }).then((responseBasketStoreData) => {
      basketStore.setData(responseBasketStoreData.data);

      if (qty > 0 && product.quantity !== qty && responseBasketStoreData.data.trackingData) {
        let trackingData = responseBasketStoreData.data.trackingData;
        trackingData.quantity = product.quantity > qty ? product.quantity - qty : qty - product.quantity;

        if (product.quantity < qty) {
          TrackingService.trigger(TrackingService.EVENT_ADD_TO_CART, trackingData);
        }

        if (product.quantity > qty) {
          TrackingService.trigger(TrackingService.EVENT_REMOVE_FROM_CART, trackingData);
        }
      }

      pigeon.publish('basket:quantityChange', {product: product, qty: qty});
    });
  }

  removeProduct(product: BasketItem, allowReload = true) {
    const removeUrl = this.urls.remove + product.entryId + '/';
    let formData = {};
    formData['entryId'] = product.entryId;
    formData['title'] = document.title;

    return httpRequest(removeUrl, {
      method: 'POST',
      headers: jsGlobalConfig().getCsrfTokenHeader(),
      data: encodeFormData(formData)
    }).then((responseBasketStoreData) => {
      if (responseBasketStoreData.data.items.length == 0 && allowReload) {
        window.location.reload();
      }
      basketStore.setData(responseBasketStoreData.data);
      pigeon.publish('basket:change', {isEmpty: basketStore.basketItemGroups.length === 0});
      pigeon.publish('productRemovedFromCart:updateCounter', basketStore.basketItemGroupsAsList.length || 0);

      TrackingService.trigger(TrackingService.EVENT_REMOVE_FROM_CART, responseBasketStoreData.data.trackingData);
    });
  }

  voucherAdded(responseBasketStore) {
    return basketStore.setData(responseBasketStore);
  }

  removeVoucher() {
    httpRequest(this.urls.removeVoucher, {
      method: 'DELETE',
      headers: jsGlobalConfig().getCsrfTokenHeader(),
    }).then((responseBasketStoreData) => {
      basketStore.setData(responseBasketStoreData.data);
    });
  }

  removeGiftVoucher(voucherCode: string) {
    const url: string = this.urls.removeGiftVoucher + voucherCode;
    httpRequest(url, {
      method: 'DELETE',
      headers: jsGlobalConfig().getCsrfTokenHeader(),
    }).then((responseBasketStoreData) => {
      basketStore.setData(responseBasketStoreData.data);
    });
  }

  async selectFreeItem(promotionId: string, itemId: string | null = null, selected: boolean, deselectSibling: boolean = false): Promise<void | {data: BasketStore}> {
    const data: any = {
      identifier: promotionId,
      selected: selected ? 'true' : 'false',
      title: document.title
    };

    if (itemId) {
      data.itemId = itemId
    }

    if (deselectSibling) {
      const freeItemsPromotion = basketStore.freeItems?.find(promo => promo.identifier === promotionId);
      const selectedFreeItem = freeItemsPromotion?.items.find(item => item.selected);
      if (selectedFreeItem) {
        await this.selectFreeItem(promotionId, selectedFreeItem.itemId, false);
      }
    }

    return httpRequest(this.urls.selectFree, {
      method: 'POST',
      headers: jsGlobalConfig().getCsrfTokenHeader(),
      data: encodeFormData(data)
    }).then((responseBasketStoreData: XiorResponse<BasketStoreWithTracking>) => {
      basketStore.setData(responseBasketStoreData.data);

      if (responseBasketStoreData.data.trackingData) {
        const eventName = (selected) ? TrackingService.EVENT_ADD_TO_CART : TrackingService.EVENT_REMOVE_FROM_CART;
        let trackingData = responseBasketStoreData.data.trackingData;
        trackingData.price = '0.00';
        TrackingService.trigger(eventName, trackingData);
      }
    });
  }

  loadMiniBasketData() {
    return httpRequest(this.urls.miniBasket, {
      method: 'GET',
      headers: jsGlobalConfig().getCsrfTokenHeader(),
    }).then((responseBasketStoreData) => {
      basketStore.setData(responseBasketStoreData.data);
    });
  }

  updateDeliveryMode(entryId, deliveryMode) {
    const formData = {};
    formData['entryId'] = entryId;
    formData['deliveryMode'] = deliveryMode;

    httpRequest(this.urls.updateDeliveryMode, {
      method: 'POST',
      headers: jsGlobalConfig().getCsrfTokenHeader(),
      data: encodeFormData(formData)
    }).then((responseBasketStoreData) => {
      basketStore.setData(responseBasketStoreData.data);
    });
  }

  selectFavoriteWine(itemId: string) {
    httpRequest(this.urls.favoriteWine, {
      method: 'POST',
      headers: jsGlobalConfig().getCsrfTokenHeader(),
      data: encodeFormData({itemId}, true),
    }).then((responseBasketStoreData) => {
      basketStore.setData(responseBasketStoreData.data);
    });
  }

  updateFavoriteWine(requestParams: {excludedCategoryIdsParam: Array<string>, productPriceTo: string, minPositionCount: string}) {
    const params = {
      ...requestParams,
      excludedCategoryIdsParam: requestParams.excludedCategoryIdsParam.join()
    };

    return httpRequest(this.urls.favoriteWineUpdate, {
      method: 'POST',
      headers: jsGlobalConfig().getCsrfTokenHeader(),
      data: encodeFormData(params)
    }).then((response) => {
      return {
        options: response.data.choices,
        selected: response.data.preselectedItemId
      }
    });
  }
}

const basketService = new BasketService();
export default basketService;
